// frontend/src/utils/updateToastState.js

// DigitalOcean phases that mean a deploy is actively rolling out.
export const ACTIVE_PHASES = ['PENDING_BUILD', 'BUILDING', 'PENDING_DEPLOY', 'DEPLOYING'];

// Pure derivation of the update-toast state. Kept separate from the polling
// hook so the transitions are unit-testable.
//
// Inputs:
//   deployment        - latest /api/deployments/current payload (or null)
//   liveId            - id derived from /health (newest build the server reports)
//   initialId         - the id observed on first load (the build we're running)
//   completedDeployId - deployment_id of a deploy we watched finish this session
//   dismissedId       - id the user last dismissed (localStorage)
//
// Returns { status: 'hidden'|'deploying'|'ready', targetId, justDeployed }.
//
// Why completedDeployId matters: when the deploy we were watching reaches the
// terminal ACTIVE phase, we KNOW a new build is live without waiting for the
// (slower, 60s) /health poll to notice — so the toast transitions straight to
// the persistent "refresh" state instead of vanishing into the polling gap.
export function deriveToastState({ deployment, liveId, initialId, completedDeployId, dismissedId }) {
  const phase = deployment?.phase;
  const deployId = deployment?.deployment_id || null;

  const deploying = ACTIVE_PHASES.includes(phase);
  const justDeployed = !!completedDeployId && phase === 'ACTIVE' && deployId === completedDeployId;
  const staleBuild = !!liveId && initialId != null && liveId !== initialId;

  let status = 'hidden';
  let targetId = null;
  if (deploying) {
    status = 'deploying';
    targetId = `d:${deployId}`;
  } else if (justDeployed) {
    status = 'ready';
    targetId = `d:${completedDeployId}`;
  } else if (staleBuild) {
    status = 'ready';
    targetId = liveId;
  }

  if (targetId && dismissedId && dismissedId === targetId) {
    status = 'hidden';
  }

  return { status, targetId, justDeployed: status === 'ready' && justDeployed };
}

export default deriveToastState;
